'use client'

import { useCallback, useEffect, useRef } from 'react'
import {
  type CartItem,
  type CategoryDTO,
  type CheckoutProduct,
  type Currency,
  type Option
} from 'ecosystem'
import { sumCart } from 'shared-redux/state'
import useGoogleAnalytics from './use-google-analytics'
import { type GaPurchaseOrder } from './types'
import { getCartItemPrice } from './utils'

interface UseViewCartProps {
  categories: Option<CategoryDTO[]>
  cartItems: CartItem[]
  cartTotal: number
  currency: Currency
  registerViewCartEvent?: boolean
}

export const useAnalyticsCart = ({
  categories,
  cartTotal,
  cartItems,
  currency,
  registerViewCartEvent
}: UseViewCartProps) => {
  const GA = useGoogleAnalytics({ categories })
  const isMounted = useRef(false)

  const viewCartEvent = useCallback(() => {
    GA.viewCart(cartItems, cartTotal, currency)
  }, [GA, cartItems, cartTotal, currency])

  useEffect(() => {
    if (!isMounted.current && registerViewCartEvent) {
      viewCartEvent()
      isMounted.current = true
    }
  }, [GA, cartItems, cartTotal, currency, registerViewCartEvent, viewCartEvent])

  const addToCartEvent = useCallback(
    (cartItem: CartItem) => {
      const cartWithoutItem = cartItems.filter((item) => item.id !== cartItem.id)
      const qty = parseInt(String(cartItem.qty || 1))
      const itemPrice = getCartItemPrice(cartItem)
      const newCartTotal = sumCart(cartWithoutItem) + itemPrice * Number(qty)
      GA.updateCart('add', newCartTotal, currency, [
        {
          ...cartItem,
          qty
        }
      ])
    },
    [GA, cartItems, currency]
  )

  const removeFromCartEvent = useCallback(
    (cartItem: CartItem) => {
      const cartWithoutItem = cartItems.filter((item) => item.id !== cartItem.id)
      const newCartTotal = sumCart(cartWithoutItem)
      GA.updateCart('remove', newCartTotal, currency, [cartItem])
    },
    [GA, cartItems, currency]
  )

  const checkoutEvent = useCallback(
    () => GA.checkout(cartItems, cartTotal, currency),
    [GA, cartItems, cartTotal, currency]
  )

  const purchaseEvent = useCallback(
    (order: GaPurchaseOrder, items: CheckoutProduct[]) => {
      GA.purchase(order, items, currency)
    },
    [GA, currency]
  )

  return {
    checkoutEvent,
    purchaseEvent,
    addToCartEvent,
    removeFromCartEvent,
    viewCartEvent,
    isMounted: isMounted.current
  }
}
